import { Component, OnInit, DestroyRef, inject, signal, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { OidcSecurityService, PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { Toast } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { AuthService } from './core/auth/auth.service';
import { HistoryService, HistoryEntry } from './core/services/history.service';
import { NavigationService } from './core/services/navigation.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Toast, ButtonModule, Tooltip, ToggleSwitch, FormsModule],
  template: `
    <p-toast />

    @if (isAuthenticated()) {
    <div class="app-layout">
      <!-- Permanent sidebar -->
      <aside class="app-sidebar">
        <div class="app-sidebar__brand">
          <img src="vizor-icon.svg" alt="Vizor" class="app-sidebar__icon" />
          <img src="vizor-wordmark.svg" alt="Vizor" class="app-sidebar__wordmark" />
        </div>

        <!-- History section -->
        <div class="app-sidebar__section">
          <div class="app-sidebar__section-label">Historique</div>
          @if (historyEntries().length === 0) {
            <div class="app-sidebar__empty">Aucun historique</div>
          } @else {
            <div class="app-sidebar__history-list">
              @for (entry of historyEntries(); track entry.timestamp) {
                <a
                  class="app-sidebar__history-item"
                  href="javascript:void(0)"
                  (click)="onHistoryClick(entry)"
                  [pTooltip]="entry.label"
                  tooltipPosition="right"
                >
                  <i [class]="entry.icon + ' app-sidebar__history-icon app-sidebar__history-icon--' + entry.type"></i>
                  <div class="app-sidebar__history-content">
                    <span class="app-sidebar__history-label">{{ entry.label }}</span>
                    <span class="app-sidebar__history-time">{{ formatRelativeTime(entry.timestamp) }}</span>
                  </div>
                </a>
              }
            </div>
            <a
              class="app-sidebar__clear-link"
              href="javascript:void(0)"
              (click)="clearHistory()"
            >
              Effacer
            </a>
          }
        </div>

        <!-- Settings section -->
        <div class="app-sidebar__section">
          <div class="app-sidebar__section-label">Parametres</div>
          <div class="app-sidebar__setting">
            <span class="app-sidebar__setting-label">Mode sombre</span>
            <p-toggleswitch
              [ngModel]="darkMode()"
              (ngModelChange)="toggleDarkMode($event)"
            />
          </div>
        </div>

        <div class="app-sidebar__spacer"></div>

        <div class="app-sidebar__footer">
          @if (userName()) {
            <div class="app-sidebar__user">
              <div class="app-sidebar__avatar">
                {{ userName().charAt(0).toUpperCase() }}
              </div>
              <span class="app-sidebar__user-name">{{ userName() }}</span>
            </div>
          }
          <a class="app-sidebar__link app-sidebar__link--logout" href="javascript:void(0)" (click)="logout()">
            <i class="pi pi-sign-out"></i>
            <span>Deconnexion</span>
          </a>
          <div class="app-sidebar__version">v2.0.0</div>
        </div>
      </aside>

      <!-- Main area -->
      <div class="app-main">
        <router-outlet />
      </div>
    </div>
    }
  `,
  styles: `
    :host {
      display: block;
      height: 100vh;
      overflow: hidden;
    }

    .app-layout {
      display: flex;
      height: 100%;
    }

    // ── Sidebar ─────────────────────────────────────
    .app-sidebar {
      width: 220px;
      min-width: 220px;
      background: linear-gradient(180deg, #0D3B66 0%, #104D87 50%, #1B6DB5 100%);
      color: #ffffff;
      display: flex;
      flex-direction: column;
      padding: var(--vizor-spacing-sm, 8px);
      gap: var(--vizor-spacing-xs, 4px);
      overflow-y: auto;

      &__brand {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--vizor-spacing-xs, 4px);
        padding: var(--vizor-spacing-md, 16px) var(--vizor-spacing-sm, 8px);
        margin-bottom: var(--vizor-spacing-sm, 8px);
        flex-shrink: 0;
      }

      &__icon {
        width: 55px;
        height: auto;
      }

      &__wordmark {
        width: 160px;
        height: auto;
      }

      &__section {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex-shrink: 0;
      }

      &__section-label {
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        opacity: 0.5;
        padding: var(--vizor-spacing-sm, 8px) var(--vizor-spacing-sm, 8px) var(--vizor-spacing-xs, 4px);
      }

      &__empty {
        font-size: 0.75rem;
        opacity: 0.4;
        padding: 6px 12px;
        font-style: italic;
      }

      // ── History ───────────────────────────────────
      &__history-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
      }

      &__history-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        border-radius: var(--vizor-radius-md, 8px);
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 0.75rem;
        transition: all 150ms ease;
        cursor: pointer;

        &:hover {
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
        }
      }

      &__history-icon {
        font-size: 0.85rem;
        width: 18px;
        text-align: center;
        flex-shrink: 0;

        &--client {
          color: #93c5fd;
        }
        &--order {
          color: #fbbf24;
        }
        &--orderLine {
          color: #a78bfa;
        }
        &--equipment {
          color: #6ee7b7;
        }
      }

      &__history-content {
        display: flex;
        flex-direction: column;
        min-width: 0;
        gap: 1px;
      }

      &__history-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 500;
        line-height: 1.2;
      }

      &__history-time {
        font-size: 0.6rem;
        opacity: 0.45;
        line-height: 1.2;
      }

      &__clear-link {
        font-size: 0.7rem;
        color: rgba(255, 255, 255, 0.5);
        text-decoration: none;
        padding: 4px 12px;
        cursor: pointer;
        transition: color 150ms ease;

        &:hover {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: underline;
        }
      }

      // ── Settings ──────────────────────────────────
      &__setting {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
      }

      &__setting-label {
        font-size: 0.8rem;
        font-weight: 500;
        opacity: 0.85;
      }

      // ── Link (kept for logout) ────────────────────
      &__link {
        display: flex;
        align-items: center;
        gap: var(--vizor-spacing-sm, 10px);
        padding: 10px 12px;
        border-radius: var(--vizor-radius-md, 8px);
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 0.875rem;
        font-weight: 500;
        transition: all 150ms ease;
        cursor: pointer;

        i {
          font-size: 1.1rem;
          width: 20px;
          text-align: center;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
        }

        &--logout {
          margin-top: var(--vizor-spacing-xs, 4px);

          &:hover {
            background: rgba(255, 255, 255, 0.12);
            color: #ffffff;
          }
        }
      }

      &__spacer {
        flex: 1;
      }

      &__footer {
        display: flex;
        flex-direction: column;
        gap: var(--vizor-spacing-xs, 4px);
        border-top: 1px solid rgba(255, 255, 255, 0.15);
        padding-top: var(--vizor-spacing-md, 16px);
        margin-top: var(--vizor-spacing-sm, 8px);
        flex-shrink: 0;
      }

      &__user {
        display: flex;
        align-items: center;
        gap: var(--vizor-spacing-sm, 8px);
        padding: 8px 12px;
      }

      &__avatar {
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.85rem;
        flex-shrink: 0;
      }

      &__user-name {
        font-size: 0.8rem;
        font-weight: 500;
        opacity: 0.9;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__version {
        font-size: 0.65rem;
        opacity: 0.4;
        text-align: center;
        padding: var(--vizor-spacing-xs, 4px);
      }
    }

    // ── Main content ────────────────────────────────
    .app-main {
      flex: 1;
      overflow: hidden;
      background: var(--vizor-background, #F5F7FA);
    }
  `,
})
export class App implements OnInit {
  private readonly oidc = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly historyService = inject(HistoryService);
  private readonly navigationService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly events = inject(PublicEventsService);

  userName = signal<string>('');
  darkMode = signal<boolean>(this._loadDarkMode());
  isAuthenticated = signal(false);

  historyEntries = signal<HistoryEntry[]>([]);

  private readonly _tabMap: Record<HistoryEntry['type'], number> = {
    client: 0,
    order: 1,
    orderLine: 2,
    equipment: 3,
  };

  constructor() {
    this.historyService.history$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((entries) => {
      this.historyEntries.set(entries);
    });

    effect(() => {
      const isDark = this.darkMode();

      // Disable transitions during theme switch for instant change
      document.documentElement.classList.add('theme-switching');

      const targets = [document.documentElement, document.body];
      targets.forEach((el) => {
        if (isDark) {
          el.classList.add('app-dark');
        } else {
          el.classList.remove('app-dark');
        }
      });

      localStorage.setItem('vizor-dark-mode', JSON.stringify(isDark));

      // Re-enable transitions after repaint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('theme-switching');
        });
      });
    });
  }

  ngOnInit(): void {
    this.oidc.isAuthenticated$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ isAuthenticated }) => {
      this.isAuthenticated.set(isAuthenticated);
    });

    this.oidc.userData$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ userData }) => {
      if (userData) {
        const name = userData.name
          || `${userData.given_name ?? ''} ${userData.family_name ?? ''}`.trim()
          || userData.preferred_username
          || userData.email
          || '';
        this.userName.set(name);
      }
    });

    // Handle silent renew errors - force re-login instead of infinite loop
    this.events
      .registerForEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type === EventTypes.SilentRenewFailed) {
          console.warn('[Auth] Silent renew failed, redirecting to login');
          this.oidc.logoffAndRevokeTokens().subscribe({
            complete: () => this.oidc.authorize(),
            error: () => this.oidc.authorize(),
          });
        }
      });
  }

  toggleDarkMode(value: boolean): void {
    this.darkMode.set(value);
  }

  onHistoryClick(entry: HistoryEntry): void {
    const tab = this._tabMap[entry.type];
    this.navigationService.navigate({
      tab,
      searchTerm: entry.term,
      searchType: entry.searchType,
    });
  }

  clearHistory(): void {
    this.historyService.clear();
  }

  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "A l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffHour < 24) return `Il y a ${diffHour} h`;
    if (diffDay < 7) return `Il y a ${diffDay} j`;
    return new Date(date).toLocaleDateString('fr-FR');
  }

  logout(): void {
    this.authService.logout();
  }

  private _loadDarkMode(): boolean {
    try {
      return JSON.parse(localStorage.getItem('vizor-dark-mode') ?? 'false');
    } catch {
      return false;
    }
  }
}
